import { Typography, Box } from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@Components/Button';
import MainSectionLayout from '@Components/MainSectionLayout';
import PopOverMenu, { MenuItem } from '@Components/PopOverMenu';
import SearchBar from '@Components/SearchBar';

import ReportsLayoutProps from './ReportsLayout.types';

const ReportsLayout: FunctionComponent<ReportsLayoutProps> = (
  props: ReportsLayoutProps
) => {
  const { t } = useTranslation();
  const {
    heading,
    searchProps,
    newReportBtnProps,
    showFiltersBtnProps,
    exportAsPdfBtnProps,
    exportAsCsvBtnProps,
    isAdmin,
    selectedBtnProps,
    exportAllBtnProps,
    clearFilterBtnProps,
    exportAllAsCsvOnClick,
    exportAllAsPdfOnClick,
    ...resProps
  } = props;

  /** STATES AND VARIABLES */
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);

  /** HANDLES CLOSING OF POP OVER MENU */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /** MENU OPTIONS OF POP OVER MENU */
  const items: MenuItem[] = [
    {
      label: t('reports.export_pdf_file'),
      value: 'pdf',
      onClick: exportAllAsPdfOnClick,
    },
    {
      label: t('reports.export_csv_file'),
      value: 'csv',
      onClick: exportAllAsCsvOnClick,
    },
  ];

  /** Page Header */
  const headingComponent = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mb={1.6}
    >
      <Typography mb={2.4} variant="h1">
        {heading}
      </Typography>
      <Button
        icon="speakerNotes"
        text={t('reports.New Report')}
        {...newReportBtnProps}
      />
    </Box>
  );

  /** Table Filters */
  const filtersComponent = (
    <Box>
      <SearchBar
        {...searchProps}
        placeholder={t('common.tableFilters.Search by keyword')}
        maxWidth={33.3}
      />
      <Box display="flex" alignItems="center" mt={2.4}>
        {!!selectedBtnProps?.text && (
          <Box display="flex" alignItems="center">
            <Box display="flex" alignItems="center">
              <Button
                variant="text"
                color="inherit"
                icon="checkboxFilled"
                iconColor="primary"
                sx={{ textTransform: 'none' }}
                {...selectedBtnProps}
                text={`${selectedBtnProps.text} ${t(
                  'common.tableFilters.Selected'
                )}`}
              />
              <Button
                variant="text"
                color="inherit"
                icon="pdf"
                text={t('reports.export_pdf')}
                {...exportAsPdfBtnProps}
                sx={{ textTransform: 'none' }}
              />
              <Button
                variant="text"
                color="inherit"
                icon="csv"
                text={t('reports.export_csv')}
                sx={{ textTransform: 'none' }}
                {...exportAsCsvBtnProps}
              />
              <Button
                variant="text"
                color="secondary"
                text={t('reports.Clear')}
                {...clearFilterBtnProps}
              />
            </Box>
          </Box>
        )}
        {showFiltersBtnProps && (
          <Button
            variant="text"
            color="secondary"
            icon="filterAltOutlined"
            text={t('reports.Show Filters')}
            sx={{ ml: 'auto', textTransform: 'none' }}
            {...showFiltersBtnProps}
          />
        )}
        <Button
          variant="outlined"
          color="secondary"
          icon="fileDownload"
          text={t('common.messages.Export All')}
          sx={{ ml: showFiltersBtnProps ? 1.6 : 'auto' }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          {...exportAllBtnProps}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <PopOverMenu
        open={isOpen}
        menuItems={items}
        anchorEl={anchorEl}
        onClose={handleClose}
      />
      <MainSectionLayout
        heading={headingComponent}
        filters={filtersComponent}
        {...resProps}
      />
    </>
  );
};

export default ReportsLayout;
